import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AtSign,
  Check,
  Download,
  Flag,
  Link2,
  ListChecks,
  MessageSquare,
  Paperclip,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { seguimientoApi } from "./api";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { formatDate, formatDateTime } from "../applications/shared/format";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { cn } from "@/lib/utils/cn";
import { Avatar, Badge, FieldLabel } from "./ui";
import { PRIORITY_LABELS, PRIORITY_TONE, TASK_AREAS, TASK_COLUMNS } from "./labels";
import type {
  BusinessArea,
  ChecklistItem,
  Sprint,
  Task,
  TaskPriority,
  TaskStatus,
  TeamMember,
} from "./types";

const ALLOWED_MIMES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function TareasPane() {
  const qc = useQueryClient();
  const { push } = useToast();
  const [businessAreaFilter, setBusinessAreaFilter] = useState("");
  const [assignee, setAssignee] = useState("");
  const [sprintFilter, setSprintFilter] = useState("");
  const [editing, setEditing] = useState<Task | null | "new">(null);
  const [dragOver, setDragOver] = useState<TaskStatus | null>(null);

  const teamQuery = useQuery({ queryKey: ["seg", "team"], queryFn: seguimientoApi.team });
  const tasksQuery = useQuery({ queryKey: ["seg", "tasks"], queryFn: () => seguimientoApi.tasks() });
  // {all:1} → incluye archivadas, para que las cards con un área archivada
  // conserven su color/nombre (el board no las pierde al archivar el área).
  const areasQuery = useQuery({
    queryKey: ["seg", "businessAreas", "all"],
    queryFn: () => seguimientoApi.businessAreas({ all: 1 }),
  });
  const sprintsQuery = useQuery({
    queryKey: ["seg", "sprints", "activo"],
    queryFn: () => seguimientoApi.sprints({ status: "activo" }),
  });

  const areas = areasQuery.data ?? [];
  const activeAreas = areas.filter((a) => a.active); // para selects / filtro
  const activeSprints = sprintsQuery.data ?? [];
  const areaById = new Map(areas.map((a) => [a.id, a])); // incluye archivadas
  const sprintById = new Map(activeSprints.map((s) => [s.id, s]));

  const move = useMutation({
    mutationFn: (args: { id: number; status: TaskStatus; orderIndex: number }) =>
      seguimientoApi.moveTask(args.id, { status: args.status, orderIndex: args.orderIndex }),
    onSuccess: (_d, args) => {
      qc.invalidateQueries({ queryKey: ["seg", "tasks"] });
      qc.invalidateQueries({ queryKey: ["seg", "metrics"] });
      qc.invalidateQueries({ queryKey: ["seg", "task-detail", args.id] });
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo mover." }),
  });

  const selectedSprint =
    sprintFilter && sprintFilter !== "none"
      ? activeSprints.find((s) => String(s.id) === sprintFilter)
      : undefined;

  const closeSprint = useMutation({
    mutationFn: (id: number) => seguimientoApi.closeSprint(id),
    onSuccess: (r) => {
      push({
        kind: "success",
        message: `Hito cerrado: ${r.completed} completada(s), ${r.carriedOver} al backlog.`,
      });
      setSprintFilter("");
      qc.invalidateQueries({ queryKey: ["seg", "tasks"] });
      qc.invalidateQueries({ queryKey: ["seg", "metrics"] });
      qc.invalidateQueries({ queryKey: ["seg", "sprints"] });
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo cerrar." }),
  });

  const all = tasksQuery.data ?? [];
  const filtered = all.filter((t) => {
    if (businessAreaFilter === "none" && t.businessAreaId != null) return false;
    if (businessAreaFilter && businessAreaFilter !== "none" && String(t.businessAreaId) !== businessAreaFilter)
      return false;
    if (assignee && String(t.assigneeUserId) !== assignee) return false;
    if (sprintFilter === "none" && t.sprintId != null) return false;
    if (sprintFilter && sprintFilter !== "none" && String(t.sprintId) !== sprintFilter) return false;
    return true;
  });

  const onDropTo = (status: TaskStatus) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(null);
    const id = Number(e.dataTransfer.getData("text/plain"));
    if (!id) return;
    const task = all.find((t) => t.id === id);
    if (!task || task.status === status) return;
    const count = all.filter((t) => t.status === status).length;
    move.mutate({ id, status, orderIndex: count });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={businessAreaFilter}
          onChange={(e) => setBusinessAreaFilter(e.target.value)}
          className="nx-input w-auto"
        >
          <option value="">Todas las áreas</option>
          {activeAreas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
          <option value="none">Sin área</option>
        </select>
        <select
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          className="nx-input w-auto"
        >
          <option value="">Todos</option>
          {(teamQuery.data ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
        <select
          value={sprintFilter}
          onChange={(e) => setSprintFilter(e.target.value)}
          className="nx-input w-auto"
        >
          <option value="">Todos los sprints</option>
          {activeSprints.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
          <option value="none">Sin sprint</option>
        </select>
        {selectedSprint && (
          <button
            onClick={async () => {
              const ok = await confirmAction({
                title: `Cerrar hito "${selectedSprint.name}"`,
                message:
                  "Las tareas sin terminar volverán al Backlog y se congela el performance del sprint (no se puede reabrir).",
                confirmLabel: "Cerrar hito",
                tone: "danger",
              });
              if (ok) closeSprint.mutate(selectedSprint.id);
            }}
            className="nx-btn-secondary"
            disabled={closeSprint.isPending}
          >
            <Flag size={14} /> Cerrar hito
          </button>
        )}
        <button
          onClick={() => tasksQuery.refetch()}
          className="nx-btn-secondary ml-auto"
          disabled={tasksQuery.isFetching}
        >
          <RefreshCw size={14} className={tasksQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
        <button onClick={() => setEditing("new")} className="nx-btn-primary">
          <Plus size={14} /> Nueva tarea
        </button>
      </div>

      {tasksQuery.isLoading ? (
        <LoadingBlock label="Cargando tablero…" />
      ) : tasksQuery.isError ? (
        <ErrorBlock onRetry={() => tasksQuery.refetch()} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
          {TASK_COLUMNS.map((col) => {
            const items = filtered
              .filter((t) => t.status === col.value)
              .sort((a, b) => a.orderIndex - b.orderIndex);
            return (
              <div
                key={col.value}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(col.value);
                }}
                onDragLeave={() => setDragOver((s) => (s === col.value ? null : s))}
                onDrop={onDropTo(col.value)}
                className={cn(
                  "flex flex-col rounded-md bg-bg-subtle/60 p-2 min-h-[120px] transition-colors",
                  dragOver === col.value && "ring-2 ring-accent/60 bg-bg-subtle",
                )}
              >
                <div className="flex items-center justify-between px-1 mb-2">
                  <span className="text-xs font-medium text-fg">{col.label}</span>
                  <Badge tone={col.value === "listo" ? "success" : "neutral"}>{items.length}</Badge>
                </div>
                <div className="flex flex-col gap-2">
                  {items.map((t) => (
                    <KanbanCard
                      key={t.id}
                      task={t}
                      area={t.businessAreaId != null ? areaById.get(t.businessAreaId) : undefined}
                      sprintName={t.sprintId != null ? sprintById.get(t.sprintId)?.name : undefined}
                      onClick={() => setEditing(t)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing !== null && (
        <TaskDrawer
          task={editing === "new" ? null : editing}
          team={teamQuery.data ?? []}
          allTasks={all}
          areas={activeAreas}
          sprints={activeSprints}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function KanbanCard({
  task,
  area,
  sprintName,
  onClick,
}: {
  task: Task;
  area?: BusinessArea;
  sprintName?: string;
  onClick: () => void;
}) {
  const checkDone = (task.checklist ?? []).filter((c) => c.done).length;
  const checkTotal = (task.checklist ?? []).length;
  const hasMeta =
    (task.attachmentCount ?? 0) > 0 || (task.commentCount ?? 0) > 0 || checkTotal > 0 || !!task.dueDate;

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", String(task.id));
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={onClick}
      className="bg-bg-card border border-border rounded-md p-2.5 cursor-pointer hover:border-border-strong transition-colors"
      // Borde izquierdo con el color del área de negocio (solo en borde, nunca
      // como fondo de texto → sin problemas de contraste).
      style={area ? { borderLeftColor: area.colorHex, borderLeftWidth: 3 } : undefined}
    >
      <div className={cn("text-xs text-fg mb-2", task.status === "listo" && "line-through opacity-70")}>
        {task.title}
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          {area && (
            <span className="text-2xs px-1.5 py-0.5 rounded bg-bg-subtle text-fg inline-flex items-center gap-1">
              <span
                className="inline-block h-2 w-2 rounded-full shrink-0"
                style={{ background: area.colorHex }}
              />
              {area.name}
            </span>
          )}
          {sprintName && (
            <span className="text-2xs px-1.5 py-0.5 rounded bg-bg-subtle text-fg-subtle inline-flex items-center gap-1">
              <Flag size={9} /> {sprintName}
            </span>
          )}
          {task.area && (
            <span className="text-2xs px-1.5 py-0.5 rounded bg-bg-subtle text-fg-subtle">
              {task.area}
            </span>
          )}
          {task.priority === "alta" && <Badge tone={PRIORITY_TONE.alta}>Alta</Badge>}
        </div>
        {task.assignee && <Avatar name={task.assignee.name} size={20} />}
      </div>
      {hasMeta && (
        <div className="flex items-center gap-2.5 mt-2 pt-2 border-t border-border text-2xs text-fg-subtle">
          {task.dueDate && <span>{formatDate(task.dueDate)}</span>}
          {checkTotal > 0 && (
            <span className="inline-flex items-center gap-1">
              <ListChecks size={12} /> {checkDone}/{checkTotal}
            </span>
          )}
          {(task.attachmentCount ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1">
              <Paperclip size={12} /> {task.attachmentCount}
            </span>
          )}
          {(task.commentCount ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1">
              <MessageSquare size={12} /> {task.commentCount}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

type Form = {
  title: string;
  description: string;
  area: string;
  businessAreaId: string;
  sprintId: string;
  status: TaskStatus;
  assigneeUserId: string;
  priority: TaskPriority;
  dueDate: string;
};

function TaskDrawer({
  task,
  team,
  allTasks,
  areas,
  sprints,
  onClose,
}: {
  task: Task | null;
  team: TeamMember[];
  allTasks: Task[];
  areas: BusinessArea[];
  sprints: Sprint[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { push } = useToast();
  const isNew = task === null;
  const taskId = task?.id ?? null;

  const [form, setForm] = useState<Form>({
    title: task?.title ?? "",
    description: task?.description ?? "",
    area: task?.area ?? "",
    businessAreaId: task?.businessAreaId ? String(task.businessAreaId) : "",
    sprintId: task?.sprintId ? String(task.sprintId) : "",
    status: task?.status ?? "pendiente",
    assigneeUserId: task?.assigneeUserId ? String(task.assigneeUserId) : "",
    priority: task?.priority ?? "media",
    dueDate: task?.dueDate ? new Date(task.dueDate).toISOString().slice(0, 10) : "",
  });

  // Estado vivo de las secciones ricas (auto-guardan).
  const [checklist, setChecklist] = useState<ChecklistItem[]>(task?.checklist ?? []);
  const [linkedTaskIds, setLinkedTaskIds] = useState<number[]>(task?.linkedTaskIds ?? []);
  const [newCheckItem, setNewCheckItem] = useState("");
  const [commentBody, setCommentBody] = useState("");
  const [mentioned, setMentioned] = useState<number[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const detailKey = ["seg", "task-detail", taskId];
  const detailQuery = useQuery({
    queryKey: detailKey,
    queryFn: () => seguimientoApi.taskDetail(taskId as number),
    enabled: !isNew && taskId !== null,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["seg", "tasks"] });
    qc.invalidateQueries({ queryKey: ["seg", "metrics"] });
    if (taskId) qc.invalidateQueries({ queryKey: detailKey });
  };

  // Patch parcial vivo (checklist / linkedTaskIds).
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => seguimientoApi.updateTask(taskId as number, body),
    onSuccess: () => invalidate(),
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo guardar." }),
  });

  const saveForm = useMutation({
    mutationFn: () => {
      const body = {
        title: form.title.trim(),
        description: form.description || null,
        area: form.area || null,
        businessAreaId: form.businessAreaId ? Number(form.businessAreaId) : null,
        sprintId: form.sprintId ? Number(form.sprintId) : null,
        status: form.status,
        assigneeUserId: form.assigneeUserId ? Number(form.assigneeUserId) : null,
        priority: form.priority,
        dueDate: form.dueDate ? `${form.dueDate}T12:00:00.000Z` : null,
      };
      return isNew
        ? seguimientoApi.createTask({ ...body, checklist, linkedTaskIds })
        : seguimientoApi.updateTask(taskId as number, body);
    },
    onSuccess: () => {
      push({ kind: "success", message: isNew ? "Tarea creada." : "Tarea guardada." });
      invalidate();
      onClose();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo guardar." }),
  });

  const del = useMutation({
    mutationFn: () => seguimientoApi.deleteTask(taskId as number),
    onSuccess: () => {
      push({ kind: "success", message: "Tarea eliminada." });
      invalidate();
      onClose();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo eliminar." }),
  });

  const addComment = useMutation({
    mutationFn: () =>
      seguimientoApi.addTaskComment(taskId as number, { body: commentBody.trim(), mentionedUserIds: mentioned }),
    onSuccess: () => {
      setCommentBody("");
      setMentioned([]);
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo comentar." }),
  });

  const delComment = useMutation({
    mutationFn: (commentId: number) => seguimientoApi.deleteTaskComment(commentId),
    onSuccess: () => invalidate(),
  });

  const upload = useMutation({
    mutationFn: (file: File) => seguimientoApi.uploadTaskAttachment(taskId as number, file),
    onSuccess: () => {
      push({ kind: "success", message: "Archivo adjuntado." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo subir." }),
  });

  const delAttachment = useMutation({
    mutationFn: (path: string) => seguimientoApi.deleteTaskAttachment(taskId as number, path),
    onSuccess: () => invalidate(),
  });

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  // ── Checklist (vivo) ──
  const persistChecklist = (next: ChecklistItem[]) => {
    setChecklist(next);
    if (!isNew) patch.mutate({ checklist: next });
  };
  const addCheckItem = () => {
    const text = newCheckItem.trim();
    if (!text) return;
    const cid =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `ci_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    persistChecklist([...checklist, { id: cid, text, done: false }]);
    setNewCheckItem("");
  };
  const toggleCheck = (id: string) =>
    persistChecklist(checklist.map((c) => (c.id === id ? { ...c, done: !c.done } : c)));
  const removeCheck = (id: string) => persistChecklist(checklist.filter((c) => c.id !== id));

  // ── Tareas vinculadas (vivo) ──
  const persistLinks = (next: number[]) => {
    setLinkedTaskIds(next);
    if (!isNew) patch.mutate({ linkedTaskIds: next });
  };
  const linkableTasks = allTasks.filter((t) => t.id !== taskId && !linkedTaskIds.includes(t.id));
  const linkedRefs = linkedTaskIds
    .map((id) => allTasks.find((t) => t.id === id))
    .filter((t): t is Task => !!t);

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!ALLOWED_MIMES.includes(f.type)) {
      push({ kind: "error", message: "Formato no permitido (PDF, JPG, PNG o WEBP)." });
      return;
    }
    if (f.size > MAX_BYTES) {
      push({ kind: "error", message: "El archivo supera los 10 MB." });
      return;
    }
    upload.mutate(f);
  };

  const checkDone = checklist.filter((c) => c.done).length;
  const attachments = detailQuery.data?.attachments ?? [];
  const comments = detailQuery.data?.comments ?? [];

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[520px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <h2 className="nx-h2 truncate">{isNew ? "Nueva tarea" : "Editar tarea"}</h2>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
          {/* Form base */}
          <div className="flex flex-col gap-3">
            <div>
              <FieldLabel>Título</FieldLabel>
              <input
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                className="nx-input"
                placeholder="Qué hay que hacer"
              />
            </div>
            <div>
              <FieldLabel>Descripción</FieldLabel>
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                className="nx-input resize-y"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>Área de negocio</FieldLabel>
                <select
                  value={form.businessAreaId}
                  onChange={(e) => set("businessAreaId", e.target.value)}
                  className="nx-input"
                >
                  <option value="">— Sin área —</option>
                  {areas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <FieldLabel>Estado</FieldLabel>
                <select
                  value={form.status}
                  onChange={(e) => set("status", e.target.value as TaskStatus)}
                  className="nx-input"
                >
                  {TASK_COLUMNS.map((cc) => (
                    <option key={cc.value} value={cc.value}>
                      {cc.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>Sprint</FieldLabel>
                <select
                  value={form.sprintId}
                  onChange={(e) => set("sprintId", e.target.value)}
                  className="nx-input"
                >
                  <option value="">— Sin sprint —</option>
                  {sprints.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <FieldLabel>Etiqueta (libre)</FieldLabel>
                <input
                  list="seg-task-areas"
                  value={form.area}
                  onChange={(e) => set("area", e.target.value)}
                  className="nx-input"
                  placeholder="Branding…"
                />
                <datalist id="seg-task-areas">
                  {TASK_AREAS.map((a) => (
                    <option key={a} value={a} />
                  ))}
                </datalist>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>Responsable</FieldLabel>
                <select
                  value={form.assigneeUserId}
                  onChange={(e) => set("assigneeUserId", e.target.value)}
                  className="nx-input"
                >
                  <option value="">— Sin asignar —</option>
                  {team.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <FieldLabel>Prioridad</FieldLabel>
                <select
                  value={form.priority}
                  onChange={(e) => set("priority", e.target.value as TaskPriority)}
                  className="nx-input"
                >
                  {(Object.keys(PRIORITY_LABELS) as TaskPriority[]).map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_LABELS[p]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <FieldLabel>Fecha límite</FieldLabel>
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => set("dueDate", e.target.value)}
                className="nx-input"
              />
            </div>
          </div>

          {isNew ? (
            <p className="text-2xs text-fg-subtle border-t border-border pt-3">
              Guardá la tarea para poder sumarle checklist, adjuntos, tareas vinculadas y comentarios.
            </p>
          ) : (
            <>
              {/* Checklist */}
              <Section icon={<ListChecks size={14} />} title="Checklist" count={checklist.length ? `${checkDone}/${checklist.length}` : undefined}>
                {checklist.length > 0 && (
                  <div className="h-1.5 rounded-full bg-bg-subtle overflow-hidden mb-2">
                    <div
                      className="h-full rounded-full bg-accent transition-all"
                      style={{ width: `${checklist.length ? (checkDone / checklist.length) * 100 : 0}%` }}
                    />
                  </div>
                )}
                <div className="flex flex-col gap-1.5">
                  {checklist.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 group">
                      <input
                        type="checkbox"
                        checked={c.done}
                        onChange={() => toggleCheck(c.id)}
                        className="shrink-0"
                      />
                      <span className={cn("text-sm flex-1", c.done ? "line-through text-fg-subtle" : "text-fg")}>
                        {c.text}
                      </span>
                      <button
                        onClick={() => removeCheck(c.id)}
                        className="nx-btn-ghost p-1 opacity-0 group-hover:opacity-100"
                        aria-label="Quitar"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    value={newCheckItem}
                    onChange={(e) => setNewCheckItem(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addCheckItem()}
                    className="nx-input text-sm"
                    placeholder="Agregar ítem…"
                  />
                  <button onClick={addCheckItem} className="nx-btn-secondary shrink-0" disabled={!newCheckItem.trim()}>
                    <Plus size={14} />
                  </button>
                </div>
              </Section>

              {/* Adjuntos */}
              <Section icon={<Paperclip size={14} />} title="Adjuntos" count={attachments.length || undefined}>
                <div className="flex flex-col gap-1.5">
                  {attachments.map((a) => (
                    <div key={a.path} className="flex items-center gap-2 text-sm group">
                      <Paperclip size={13} className="text-fg-subtle shrink-0" />
                      <span className="text-fg truncate flex-1">{a.filename}</span>
                      <span className="text-2xs text-fg-subtle shrink-0">{fmtBytes(a.size)}</span>
                      {a.signedUrl && (
                        <a
                          href={a.signedUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="nx-btn-ghost p-1"
                          aria-label="Descargar"
                        >
                          <Download size={13} />
                        </a>
                      )}
                      <button
                        onClick={async () => {
                          const ok = await confirmAction({ title: "Eliminar adjunto", confirmLabel: "Eliminar", tone: "danger" });
                          if (ok) delAttachment.mutate(a.path);
                        }}
                        className="nx-btn-ghost p-1 opacity-0 group-hover:opacity-100"
                        aria-label="Eliminar"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {attachments.length === 0 && !detailQuery.isLoading && (
                    <p className="text-2xs text-fg-subtle">Sin adjuntos.</p>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,image/jpeg,image/png,image/webp"
                  onChange={onPickFile}
                  className="hidden"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="nx-btn-secondary mt-2"
                  disabled={upload.isPending}
                >
                  <Paperclip size={14} /> {upload.isPending ? "Subiendo…" : "Adjuntar archivo"}
                </button>
              </Section>

              {/* Tareas vinculadas */}
              <Section icon={<Link2 size={14} />} title="Tareas vinculadas" count={linkedRefs.length || undefined}>
                <div className="flex flex-wrap gap-1.5">
                  {linkedRefs.map((lt) => (
                    <span
                      key={lt.id}
                      className="inline-flex items-center gap-1.5 text-2xs px-2 py-1 rounded bg-bg-subtle text-fg"
                    >
                      <Link2 size={11} className="text-fg-subtle" />
                      {lt.title}
                      <button
                        onClick={() => persistLinks(linkedTaskIds.filter((id) => id !== lt.id))}
                        aria-label="Desvincular"
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                  {linkedRefs.length === 0 && <span className="text-2xs text-fg-subtle">Ninguna.</span>}
                </div>
                {linkableTasks.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => {
                      const id = Number(e.target.value);
                      if (id) persistLinks([...linkedTaskIds, id]);
                    }}
                    className="nx-input mt-2 text-sm"
                  >
                    <option value="">+ Vincular una tarea…</option>
                    {linkableTasks.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title}
                      </option>
                    ))}
                  </select>
                )}
              </Section>

              {/* Comentarios */}
              <Section icon={<MessageSquare size={14} />} title="Comentarios" count={comments.length || undefined}>
                <div className="flex flex-col gap-3">
                  {comments.map((cm) => (
                    <div key={cm.id} className="flex gap-2 group">
                      <Avatar name={cm.author?.name} size={24} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-fg">{cm.author?.name ?? "—"}</span>
                          <span className="text-2xs text-fg-subtle">{formatDateTime(cm.createdAt)}</span>
                          <button
                            onClick={() => delComment.mutate(cm.id)}
                            className="nx-btn-ghost p-0.5 ml-auto opacity-0 group-hover:opacity-100"
                            aria-label="Eliminar comentario"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                        <p className="text-sm text-fg whitespace-pre-wrap break-words">{cm.body}</p>
                        {cm.mentioned.filter(Boolean).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {cm.mentioned.filter(Boolean).map((m) => (
                              <span key={m!.id} className="text-2xs text-accent inline-flex items-center gap-0.5">
                                <AtSign size={10} />
                                {m!.name.split(" ")[0]}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {comments.length === 0 && !detailQuery.isLoading && (
                    <p className="text-2xs text-fg-subtle">Sin comentarios todavía.</p>
                  )}
                </div>

                {/* Composer */}
                <div className="mt-3 border-t border-border pt-3">
                  <textarea
                    rows={2}
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                    className="nx-input resize-y text-sm"
                    placeholder="Escribí un comentario…"
                  />
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="text-2xs text-fg-subtle inline-flex items-center gap-1">
                      <AtSign size={11} /> Mencionar:
                    </span>
                    {team.map((m) => {
                      const on = mentioned.includes(m.id);
                      return (
                        <button
                          key={m.id}
                          onClick={() =>
                            setMentioned((prev) =>
                              on ? prev.filter((x) => x !== m.id) : [...prev, m.id],
                            )
                          }
                          className={cn(
                            "text-2xs px-2 py-0.5 rounded-full border transition-colors",
                            on
                              ? "bg-accent-soft text-accent border-accent/40"
                              : "border-border text-fg-muted hover:text-fg",
                          )}
                        >
                          {m.name.split(" ")[0]}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => commentBody.trim() && addComment.mutate()}
                      disabled={!commentBody.trim() || addComment.isPending}
                      className="nx-btn-primary ml-auto"
                    >
                      <Send size={13} /> Comentar
                    </button>
                  </div>
                </div>
              </Section>
            </>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-border bg-bg-surface flex items-center gap-2 justify-end">
          {!isNew && (
            <button
              onClick={async () => {
                const ok = await confirmAction({
                  title: "Eliminar tarea",
                  message: `Se borra "${task!.title}" con sus comentarios y adjuntos.`,
                  confirmLabel: "Eliminar",
                  tone: "danger",
                });
                if (ok) del.mutate();
              }}
              disabled={del.isPending}
              className="nx-btn-danger mr-auto"
            >
              <Trash2 size={14} /> Eliminar
            </button>
          )}
          <button onClick={onClose} className="nx-btn-secondary">
            Cerrar
          </button>
          <button
            onClick={() => {
              if (!form.title.trim()) {
                push({ kind: "error", message: "El título es obligatorio." });
                return;
              }
              saveForm.mutate();
            }}
            disabled={saveForm.isPending}
            className="nx-btn-primary"
          >
            <Check size={14} /> {isNew ? "Crear" : "Guardar"}
          </button>
        </footer>
      </aside>
    </>
  );
}

function Section({
  icon,
  title,
  count,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  count?: string | number;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-border pt-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-fg-muted">{icon}</span>
        <h3 className="text-xs font-medium text-fg">{title}</h3>
        {count !== undefined && <Badge tone="neutral">{count}</Badge>}
      </div>
      {children}
    </div>
  );
}
